import { Badge, ManageableItemsList, RichTextarea } from '@payaca/ambrosia';
import { currencyPrice } from '@payaca/helpers/financeHelper';
import { getRegionalTextString } from '@payaca/helpers/internationalHelper';
import { getPaymentScheduleStagesWithValues } from '@payaca/helpers/paymentScheduleHelper';
import { AccountRegions } from '@payaca/types/accountTypes';
import { RegionalStrings } from '@payaca/types/internationalTypes';
import { PaymentSchedule } from '@payaca/types/payment-schedule';
import { Nullish } from '@payaca/utilities/types';
import { useMemo } from 'react';

type Props = {
  paymentSchedule: Nullish<PaymentSchedule>;
  projectValue?: number;
  region?: AccountRegions;
};
const PaymentScheduleSummary = ({
  paymentSchedule,
  projectValue,
  region,
}: Props) => {
  const stages = useMemo(
    () =>
      paymentSchedule?.stages
        ? getPaymentScheduleStagesWithValues(paymentSchedule, projectValue || 0)
        : [],
    [paymentSchedule, projectValue]
  );

  const currencySymbol = useMemo(() => {
    return getRegionalTextString(region, RegionalStrings.CURRENCY_SYMBOL);
  }, [region]);

  return (
    <ManageableItemsList>
      <ManageableItemsList.Table
        uniqueKey="description"
        items={stages}
        hideEmptyState
        doNotUseFooterRow={
          paymentSchedule?.explainer ? (
            <div className="mx-6 my-2">
              <RichTextarea readOnly value={paymentSchedule.explainer} />
            </div>
          ) : undefined
        }
      >
        <ManageableItemsList.Table.Column
          field="description"
          header="Stage"
          className="!whitespace-normal"
          render={(field, row) => (
            <span className={row.isPaid ? 'text-gray-400' : undefined}>
              {field}
            </span>
          )}
        />
        <ManageableItemsList.Table.Column
          field="percentageDue"
          header="% Due"
          className="text-right"
          render={(field, row) => (
            <span className={row.isPaid ? 'text-gray-400' : undefined}>
              {field && `${field.toFixed(1)}%`}
            </span>
          )}
        />
        {!!projectValue && (
          <ManageableItemsList.Table.Column
            field="value"
            className="text-right"
            header={`${currencySymbol} Due`}
            render={(field, row) =>
              row.isPaid ? (
                <>
                  <Badge colour="green" variant="soft">
                    Paid
                  </Badge>
                  <span className="pl-3 text-gray-400">
                    {field !== null && currencyPrice(field, region)}
                  </span>
                </>
              ) : (
                field !== null && currencyPrice(field, region)
              )
            }
          />
        )}
      </ManageableItemsList.Table>
    </ManageableItemsList>
  );
};

export default PaymentScheduleSummary;
